// "AI audit": every model call this tab has made, newest first.
//
// The rest of the design record argues that the language model is held at arm's length — it proposes,
// the deterministic layer vets, the human commits. This section is the receipt for that argument. It
// reads the in-memory ring buffer in `src/ai/audit.ts`, which `callLLM` writes a row to for every call
// and which the validation layer annotates with what it threw away.
//
// No dropdowns (spec §8): a table and a legend line, nothing to open.

import { useSyncExternalStore } from 'react';
import { entriesNewestFirst, MODEL_LABEL, PURPOSE_LABEL, subscribe, type AuditEntry } from '../../ai/audit';

/** Wall-clock time of day, seconds included: these rows are minutes apart, not hours. */
function hhmmss(at: number): string {
  const d = new Date(at);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** "1 order", "4 orders": an audit nobody can read as English is not evidence of anything. */
function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

const SOURCE_TONE: Record<NonNullable<AuditEntry['source']>, string> = {
  llm: 'var(--color-friendly)',
  cached: 'var(--color-sustain)',
  template: 'var(--color-warn)',
};

function SourceCell({ entry }: { entry: AuditEntry }) {
  if (!entry.source) return <span className="mono text-[11px] text-fog">out…</span>;
  return (
    <span className="mono text-[11px] uppercase tracking-[0.1em]" style={{ color: SOURCE_TONE[entry.source] }}>
      {entry.source}
    </span>
  );
}

/** What the two gates did to this answer, in words, or a dash when nothing validated it. */
function ValidationCell({ entry }: { entry: AuditEntry }) {
  const bits: { text: string; tone?: string }[] = [];

  if (entry.citedTotal !== undefined) {
    const dropped = entry.citedDropped ?? 0;
    bits.push(
      dropped > 0
        ? { text: `${dropped} of ${plural(entry.citedTotal, 'citation')} dropped`, tone: 'var(--color-warn)' }
        : entry.citedTotal === 0
          ? { text: 'cited nothing' }
          : { text: `${plural(entry.citedTotal, 'citation')}, all grounded` },
    );
  }
  if (entry.flagged) bits.push({ text: 'FLAGGED — most cited ids did not exist', tone: 'var(--color-hostile)' });
  if (entry.ordersKept !== undefined) {
    const rejected = entry.ordersRejected ?? [];
    bits.push(
      rejected.length > 0
        ? { text: `${plural(entry.ordersKept, 'order')} legal, ${rejected.length} rejected`, tone: 'var(--color-warn)' }
        : { text: `${plural(entry.ordersKept, 'order')} legal` },
    );
    for (const reason of rejected) bits.push({ text: `dropped: ${reason}`, tone: 'var(--color-fog)' });
  }
  if (entry.error) bits.push({ text: entry.error.slice(0, 120), tone: 'var(--color-hostile)' });

  if (bits.length === 0) return <span className="text-fog">—</span>;
  return (
    <span className="block space-y-0.5">
      {bits.map((b, i) => (
        <span key={i} className="block text-[11px] leading-snug" style={{ color: b.tone ?? 'var(--color-paper)' }}>
          {b.text}
        </span>
      ))}
    </span>
  );
}

export function AiAudit() {
  const entries = useSyncExternalStore(subscribe, entriesNewestFirst, entriesNewestFirst);

  return (
    <div data-testid="ai-audit">
      <h1 className="mono text-[12px] uppercase tracking-[0.25em] text-paper">AI audit</h1>

      <p className="mt-3 max-w-[74ch] text-[15px] leading-relaxed text-paper">
        Everything above is a claim about how the large language model (LLM) is held: it proposes, the
        deterministic layer vets, and a human commits. This is the receipt. Every call WINDTUNNEL makes
        opens a row here — what it was for, which model, roughly how big the prompt was, how long the
        answer took, whether the answer came from the model, a recording or the fallback template, and
        what validation threw away: citations to event identifiers that do not exist, orders the
        simulation&apos;s own legality rules rejected and the reason each gave. It is an in-memory ring
        buffer of the last 200 calls in this browser tab — nothing is stored or sent anywhere — so it
        fills as you play and empties when you reload. If the table is empty, run a fight first; if it
        shows only <em>template</em>, there is no model key behind the proxy and you are watching the
        fallback path the trade-off log talks about, which is the point of having one.
      </p>

      {entries.length === 0 ? (
        <p data-testid="ai-audit-empty" className="mono mt-4 text-[12px] text-fog">
          No model calls yet in this tab. Play a fight, then come back.
        </p>
      ) : (
        <div className="prose-wt mt-4">
          <div className="prose-wt-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Purpose</th>
                  <th>For</th>
                  <th>Model</th>
                  <th>Prompt ≈tok</th>
                  <th>Cached tok</th>
                  <th>Latency</th>
                  <th>Source</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} data-testid="ai-audit-row">
                    <td className="mono whitespace-nowrap text-fog">{hhmmss(e.at)}</td>
                    <td className="whitespace-nowrap">{PURPOSE_LABEL[e.purpose]}</td>
                    <td className="mono text-fog">{e.ref ?? '—'}</td>
                    <td className="mono whitespace-nowrap">{MODEL_LABEL[e.model]}</td>
                    <td className="mono whitespace-nowrap text-right">{e.promptTokens.toLocaleString()}</td>
                    <td
                      data-testid="ai-audit-cached"
                      data-cached={e.cachedTokens ?? ''}
                      className="mono whitespace-nowrap text-right"
                      title={
                        e.cachedTokens === undefined
                          ? 'The provider reported no cache figures for this call'
                          : `${e.cachedTokens.toLocaleString()} prompt tokens were served from the provider's cache instead of being read again`
                      }
                      style={{ color: e.cachedTokens ? 'var(--color-friendly)' : undefined }}
                    >
                      {e.cachedTokens === undefined ? '—' : e.cachedTokens.toLocaleString()}
                    </td>
                    <td
                      className="mono whitespace-nowrap text-right"
                      title={
                        e.firstTextMs === undefined
                          ? 'Time from the call going out to a validated answer'
                          : `First text at ${(e.firstTextMs / 1000).toFixed(2)} s — what a reader waits for — `
                            + `and a validated answer at ${(e.latencyMs / 1000).toFixed(2)} s`
                      }
                    >
                      {e.source ? `${(e.latencyMs / 1000).toFixed(2)} s` : '—'}
                      {e.firstTextMs !== undefined && (
                        <span className="text-fog"> ↦{(e.firstTextMs / 1000).toFixed(1)}s</span>
                      )}
                    </td>
                    <td><SourceCell entry={e} /></td>
                    <td><ValidationCell entry={e} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] leading-snug text-fog">
            Prompt size is an estimate — prompt characters divided by four — not a billed figure.
            <em> Cached tok</em> is the provider&apos;s own <code>cache_read_input_tokens</code>: the part of
            the prompt served from its cache instead of read again. The advisors&apos; system block — persona
            doctrine, the commander&apos;s intent, the graphics, the task organization, the plan and the deck —
            is built to be byte-identical for a whole run so that this number is not zero.
            Where a latency carries a second figure (<em>↦1.8s</em>), the call was streamed and that is when
            the first text reached the card — the wait a reader actually experiences, as against the moment
            the answer could be validated and acted on.
            <em> llm</em> is a live answer, <em>cached</em> a recorded one replayed for the offline demo,
            <em> template</em> the deterministic fallback that runs when the model is slow, absent or
            returns something that fails its schema.
          </p>
        </div>
      )}
    </div>
  );
}
